/**
 * EventTypeTabsPanel
 *
 * Reusable tab container for browsing content organized by event type.
 * Encapsulates:
 *   - Pill-style tabs with active-only event type color
 *   - A thin accent color bar bridging tabs to content
 *   - A subtly tinted content background that reinforces the active tab
 *   - Responsive sticky header: full header on desktop, tabs-only on mobile
 *
 * Used by FlowersPage and TemplatesPage to keep the tab UX consistent.
 *
 * Sticky positioning uses `top: APP_HEADER_HEIGHT` because the AppShell header
 * is `position: fixed` and the viewport is the scroll context. The content
 * area in App.tsx must NOT have `overflow: auto/hidden/scroll` on any ancestor,
 * as that would create a new scroll context and break sticky.
 */

import type { ReactNode } from 'react';
import { Box, ScrollArea, Tabs, rem } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import type { EventType } from '../../types';
import { APP_HEADER_HEIGHT } from '../../config/layout';

/**
 * Mantine color names where shade-6 has insufficient contrast with white text.
 * For these colors the active pill uses dark text instead.
 */
const LOW_CONTRAST_COLORS = new Set(['yellow', 'lime']);

/**
 * Returns `true` when white text is legible on the shade-6 swatch of `color`.
 */
const isWhiteSafe = (color: string): boolean => !LOW_CONTRAST_COLORS.has(color);

/**
 * Shared sticky container styles.
 * Uses the AppShell header height as the `top` offset because the AppShell
 * header is `position: fixed`, so the viewport is the scroll context.
 */
const stickyStyles = {
  position: 'sticky' as const,
  top: rem(APP_HEADER_HEIGHT),
  zIndex: 10,
  background: 'var(--mantine-color-body)',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  borderRadius: `${rem(8)} ${rem(8)} 0 0`,
};

export interface EventTypeTabsPanelProps {
  /** All event types to render as tabs. */
  eventTypes: EventType[];
  /** Currently selected event type id (controlled). */
  activeEventTypeId: number | null;
  /** Called when the user clicks a different tab. */
  onTabChange: (id: number) => void;
  /**
   * Optional header content (title, subtitle, controls) rendered above the tabs.
   * On desktop this is included inside the sticky container; on mobile it scrolls
   * away and only the tabs remain sticky.
   */
  headerContent?: ReactNode;
  /** Content rendered below the tab bar inside the tinted wrapper. */
  children: ReactNode;
}

/**
 * Renders event type tabs as colored pills with an accent bar and tinted
 * content area. Only the active tab receives the event type color; inactive
 * tabs stay neutral for visual clarity.
 *
 * Desktop (> 576px): the headerContent, tabs, and accent bar are all sticky.
 * Mobile (<= 576px): only the tabs and accent bar are sticky; the header scrolls away.
 */
export const EventTypeTabsPanel = ({
  eventTypes,
  activeEventTypeId,
  onTabChange,
  headerContent,
  children,
}: EventTypeTabsPanelProps) => {
  const isMobile = useMediaQuery('(max-width: 576px)');

  const activeColor =
    eventTypes.find((et) => et.id === activeEventTypeId)?.color ?? 'indigo';

  const handleChange = (value: string | null) => {
    if (!value) return;
    onTabChange(parseInt(value, 10));
  };

  /** Tab list + accent bar — always sticky on mobile, part of full sticky block on desktop. */
  const tabBarBlock = (
    <>
      <ScrollArea type="auto" offsetScrollbars scrollbarSize={4}>
        <Tabs.List style={{ borderBottom: 'none', gap: rem(6) }}>
          {eventTypes.map((et) => {
            const isActive = et.id === activeEventTypeId;
            const whiteSafe = isWhiteSafe(et.color);

            return (
              <Tabs.Tab
                key={et.id}
                value={et.id.toString()}
                styles={{
                  tab: {
                    backgroundColor: isActive
                      ? `var(--mantine-color-${et.color}-6)`
                      : 'transparent',
                    color: isActive
                      ? whiteSafe
                        ? 'white'
                        : `var(--mantine-color-${et.color}-9)`
                      : 'var(--mantine-color-gray-7)',
                    fontWeight: isActive ? 600 : 400,
                    transition: 'background-color 200ms ease, color 200ms ease',
                    '&:hover': isActive
                      ? undefined
                      : { backgroundColor: 'var(--mantine-color-gray-1)' },
                  },
                }}
              >
                {et.name}
              </Tabs.Tab>
            );
          })}
        </Tabs.List>
      </ScrollArea>

      {/* Accent color bar — bridges tabs to content */}
      <Box
        style={{
          height: 4,
          backgroundColor: `var(--mantine-color-${activeColor}-5)`,
          transition: 'background-color 300ms ease',
          marginTop: rem(6),
        }}
      />
    </>
  );

  return (
    <Tabs
      variant="pills"
      radius="xl"
      value={activeEventTypeId?.toString() ?? null}
      onChange={handleChange}
    >
      {isMobile ? (
        <>
          {/* On mobile the header scrolls away normally */}
          {headerContent}

          {/* Only the tab bar is sticky */}
          <Box
            px="sm"
            style={{
              ...stickyStyles,
              paddingTop: rem(8),
              paddingBottom: rem(4),
            }}
          >
            {tabBarBlock}
          </Box>
        </>
      ) : (
        /* On desktop everything is in one sticky container */
        <Box
          px="sm"
          style={{
            ...stickyStyles,
            paddingTop: rem(8),
            paddingBottom: rem(4),
          }}
        >
          {headerContent}
          <Box mt="xs">{tabBarBlock}</Box>
        </Box>
      )}

      {/* Tinted content wrapper */}
      <Box
        pt="md"
        pb="md"
        px="sm"
        style={{
          backgroundColor: `color-mix(in srgb, var(--mantine-color-${activeColor}-0) 50%, transparent)`,
          transition: 'background-color 300ms ease',
          borderRadius: `0 0 ${rem(8)} ${rem(8)}`,
          minHeight: 300,
        }}
      >
        {children}
      </Box>
    </Tabs>
  );
};
